import { useState, useRef, useCallback } from "react";

function parseCsvLine(line) {
  const result = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ",") {
        result.push(current.trim());
        current = "";
      } else {
        current += ch;
      }
    }
  }
  result.push(current.trim());
  return result;
}

/**
 * @param {Object} config
 * @param {string[]} config.requiredHeaders
 * @param {string[]} [config.optionalHeaders]
 * @param {Object.<string, string>} [config.headerMap] - Maps CSV column names to normalized field names
 *   e.g. { "first name": "first_name", "last name": "last_name" }
 */
export default function useCsvParser(config) {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [error, setError] = useState("");
  const [warnings, setWarnings] = useState([]);
  const fileRef = useRef();

  const reset = useCallback(() => {
    setFile(null);
    setParsedData(null);
    setError("");
    setWarnings([]);
    if (fileRef.current) fileRef.current.value = "";
  }, []);

  const handleFile = useCallback(
    (e) => {
      setError("");
      setParsedData(null);
      setWarnings([]);

      const f = e.target.files?.[0];
      // Reset input so the same file can be re-selected
      e.target.value = "";
      if (!f) return;

      if (!f.name.endsWith(".csv")) {
        setError("Please upload a .csv file.");
        return;
      }

      setFile(f);

      if (!config) {
        setError("No resource selected.");
        return;
      }

      const headerMap = config.headerMap || {};

      const reader = new FileReader();
      reader.onload = (evt) => {
        const text = evt.target.result;
        const lines = text.split(/\r?\n/).filter((l) => l.trim());

        if (lines.length < 2) {
          setError("File must have a header row and at least one data row.");
          return;
        }

        const headers = parseCsvLine(lines[0]).map((h) => h.toLowerCase());

        // Validate required headers against raw CSV column names
        const missing = (config.requiredHeaders || []).filter((h) => !headers.includes(h));
        if (missing.length > 0) {
          setError(`Missing required columns: ${missing.join(", ")}`);
          return;
        }

        // Warn about unrecognized headers (raw names)
        const allKnown = [
          ...(config.requiredHeaders || []),
          ...(config.optionalHeaders || []),
        ];
        const extra = headers.filter((h) => !allKnown.includes(h));
        if (extra.length > 0) {
          setWarnings(extra);
        }

        // Only keep recognized headers
        const knownIndices = [];
        const knownHeaders = [];
        headers.forEach((h, i) => {
          if (allKnown.includes(h)) {
            knownIndices.push(i);
            knownHeaders.push(h);
          }
        });

        // Map known headers for row keys
        const mappedHeaders = knownHeaders.map((h) => headerMap[h] || h);

        const rows = lines.slice(1).map((line) => {
          const values = parseCsvLine(line);
          const row = {};
          mappedHeaders.forEach((h, j) => {
            row[h] = values[knownIndices[j]] || "";
          });
          return row;
        });

        setParsedData({ headers: knownHeaders, mappedHeaders, rows });
      };

      reader.readAsText(f);
    },
    [config]
  );

  return {
    file,
    parsedData,
    error,
    warnings,
    fileRef,
    handleFile,
    reset,
  };
}